import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function ExceptionTrackingPage() {
  const faqs = [
    {
      question: 'What is exception tracking?',
      answer: 'Exception tracking documents emergency changes that bypass normal controls, including justification, approval, and retrospective review.'
    },
    {
      question: 'What do auditors expect for exceptions?',
      answer: 'Auditors expect a clear business reason, approver identity, timestamps, and post-incident review evidence.'
    },
    {
      question: 'How are exceptions identified?',
      answer: 'EvidentTrail detects when branch protection rules or required reviews are bypassed and records the change context.'
    },
    {
      question: 'Do exceptions replace standard change controls?',
      answer: 'No. Exceptions are meant for emergencies and must be rare, documented, and reviewed.'
    }
  ];

  return (
    <MarketingLayout
      title="Emergency and Exception Change Tracking"
      description="Learn about emergency change tracking for audit compliance. Documentation and retrospective review of exception changes."
      h1="Emergency and Exception Change Tracking"
      heroText="Audit standards generally allow emergency changes, provided they are documented and reviewed after implementation."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Exception Tracking', url: 'https://evidenttrail.com/exception-tracking' }
        ]}
      />
      <FaqJsonLd items={faqs} />

      {/* What Auditors Expect */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>What Auditors Typically Expect</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Emergency changes (also called "exception changes" or "break-glass" changes) are permitted under most audit frameworks, including SOC 2, ISO 27001, and PCI DSS. However, they require special handling to demonstrate that the exception was justified and properly controlled. Auditors expect:
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>Before or During Emergency</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Justification:</strong> Clear business reason (e.g., "Production outage affecting 100% of users")
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Approval:</strong> Identified approver (e.g., CTO, Engineering Manager)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Timestamp:</strong> When the emergency occurred and when the fix was deployed
                    </span>
                  </li>
                </ul>
              </div>
              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-3`}>After Emergency (Retrospective)</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Retrospective Review:</strong> Post-incident review by peer or manager
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Documentation:</strong> Link to incident ticket and post-mortem
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Follow-up PR:</strong> Formal PR with tests for the emergency fix (if not done initially)
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className={`${tokens.heading.h3} mb-4`}>Example: Emergency Change</h3>
            <div className={`${tokens.card} mb-4`}>
              <div className="font-mono text-sm space-y-1 text-slate-300">
                <div><strong className="text-white">Incident:</strong> INC-567 "Database connection pool exhausted, API unresponsive"</div>
                <div><strong className="text-white">Detected:</strong> 2024-01-20 02:15 UTC (monitoring alert)</div>
                <div><strong className="text-white">Impact:</strong> 100% of API traffic returning 503 errors</div>
                <div><strong className="text-white">Approved By:</strong> jane.doe@company.com (VP Engineering, verbal approval via Slack)</div>
                <div><strong className="text-white">Fix Deployed:</strong> 2024-01-20 02:45 UTC (direct commit to main, bypassed review)</div>
                <div><strong className="text-white">Change:</strong> Increased connection pool from 10 to 50</div>
                <div><strong className="text-white">Retrospective Review:</strong> 2024-01-21 (post-mortem meeting)</div>
                <div><strong className="text-white">Follow-up PR:</strong> #1123 (added tests, refactored connection handling)</div>
              </div>
            </div>
            <p className={`${tokens.text.small} text-slate-400 italic`}>
              This example demonstrates proper exception handling: clear justification, approval, contemporaneous documentation, and retrospective review.
            </p>
            <p className={`${tokens.text.base} mt-4`}>
              For a full output example, review the{' '}
              <Link to="/sample-exception" className="text-primary-400 hover:underline">sample exception record</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Assessment Context */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How Exceptions are Assessed</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Exception tracking is assessed separately from standard change flows. Auditors understand that emergencies happen, but they look for patterns that might indicate a control weakness:
            </p>
            <div className="space-y-6 mb-8">
              <div className="border border-green-500/30 bg-green-950/20 rounded-lg p-6">
                <h3 className={`${tokens.heading.h3} mb-3 text-green-300`}>Acceptable Exception Pattern</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>✅ <strong>Low frequency:</strong> 1-5% of total changes are exceptions (e.g., 5 out of 500 changes)</li>
                  <li>✅ <strong>Justified:</strong> Each exception has clear business reason (outage, security incident)</li>
                  <li>✅ <strong>Reviewed:</strong> All exceptions reviewed within 24-48 hours</li>
                  <li>✅ <strong>Follow-up:</strong> Formal PR created for each emergency fix</li>
                  <li>✅ <strong>Documented:</strong> Link to incident ticket and post-mortem</li>
                </ul>
              </div>
              <div className="border border-red-500/30 bg-red-950/20 rounded-lg p-6">
                <h3 className={`${tokens.heading.h3} mb-3 text-red-300`}>Concerning Exception Pattern (Audit Risk)</h3>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li>❌ <strong>High frequency:</strong> &gt;10% of changes bypass controls (indicates control not effective)</li>
                  <li>❌ <strong>Convenience exceptions:</strong> "We were in a hurry" or "Too much friction" as justification</li>
                  <li>❌ <strong>No retrospective review:</strong> Exceptions not reviewed or documented</li>
                  <li>❌ <strong>Same person approves own exception:</strong> No segregation of duties</li>
                  <li>❌ <strong>Pattern of specific developers:</strong> Some developers routinely bypass controls</li>
                </ul>
              </div>
            </div>
            <p className={`${tokens.text.base}`}>
              <strong className="text-white">Audit tip:</strong> If you have more than a handful of exceptions per quarter, consider whether your standard change process has too much friction. Auditors may recommend process improvements rather than accepting frequent exceptions as normal.
            </p>
          </div>
        </div>
      </section>

      {/* How EvidentTrail Fits */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How EvidentTrail Supports Exception Tracking</h2>
            <p className={`${tokens.text.base} mb-6`}>
              EvidentTrail records when branch protection rules were bypassed or modified, identifying exception changes and capturing the context for retrospective review.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Topics */}
      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Related Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/audit-readiness" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Audit Readiness</h4>
                <p className="text-sm text-slate-400">How exception records fit into the complete audit evidence picture.</p>
              </Link>
              <Link to="/github-change-control" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">GitHub Change Control</h4>
                <p className="text-sm text-slate-400">Ongoing evidence trails for auditors.</p>
              </Link>
              <Link to="/audit-readiness" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Audit Readiness</h4>
                <p className="text-sm text-slate-400">Prepare for audit sampling windows.</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className={`${tokens.heading.h2} mb-4`}>Learn More About Exception Change Tracking</h2>
            <p className={`${tokens.text.base} mb-8`}>
              See how exception tracking supports audit compliance.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={appLinks.register} className="btn btn-primary text-lg px-8 py-3">
                Get Started
              </a>
              <Link to="/faq" className="text-slate-300 hover:text-white transition-colors">
                Security and audit FAQ →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
